import { Head, Link, router } from '@inertiajs/react'
import { useEffect, useState } from 'react'

import { CollectSessionType } from './types'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Activity, AlertCircle, Clock, DollarSign, MapPin, Search } from 'lucide-react'

interface IndexProps {
  collect_sessions: CollectSessionType[]
}

export default function Index({ collect_sessions }: IndexProps) {
  const [searchTerm, setSearchTerm] = useState(() => {
    if (typeof window === 'undefined') return ''
    return new URLSearchParams(window.location.search).get('q') ?? ''
  })
  const [statusFilter, setStatusFilter] = useState(() => {
    if (typeof window === 'undefined') return 'all'
    return new URLSearchParams(window.location.search).get('status') ?? 'all'
  })

  useEffect(() => {
    const timeout = setTimeout(() => {
      const basePath =
        typeof window === 'undefined' ? '/collect_sessions' : window.location.pathname
      router.get(
        basePath || '/collect_sessions',
        {
          q: searchTerm.trim() || undefined,
          status: statusFilter === 'all' ? undefined : statusFilter,
        },
        {
          preserveState: true,
          preserveScroll: true,
          replace: true,
          only: ['collect_sessions'],
        }
      )
    }, 300)

    return () => clearTimeout(timeout)
  }, [searchTerm, statusFilter])

  return (
    <>
      <Head title="Collect sessions" />
      <div className="p-8">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-foreground">Tax Collection Management</h2>
            <p className="text-muted-foreground">Monitor active collection sessions and devices</p>
          </div>
          <div className="flex gap-2">
            <Link href="/admin/collection/devices">
              <button className="flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm font-medium text-foreground hover:bg-secondary">
                <Activity className="h-4 w-4" />
                Collection Devices
              </button>
            </Link>
            <Link href="/admin/collection/sessions">
              <button className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90">
                <Clock className="h-4 w-4" />
                All Sessions
              </button>
            </Link>
          </div>
        </div>

        <div className="mb-6 grid gap-4 md:grid-cols-4">
          <div className="rounded-lg border border-border bg-card p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-success/10 p-2">
                <Activity className="h-5 w-5 text-success" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Active Collections</p>
                <p className="text-2xl font-bold text-foreground">0</p>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-border bg-card p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-primary/10 p-2">
                <DollarSign className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Collected Today</p>
                <p className="text-2xl font-bold text-foreground">0 FCFA</p>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-border bg-card p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-accent/10 p-2">
                <MapPin className="h-5 w-5 text-accent" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Visits</p>
                <p className="text-2xl font-bold text-foreground">0</p>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-border bg-card p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-warning/10 p-2">
                <AlertCircle className="h-5 w-5 text-warning" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Low Battery Alerts</p>
                <p className="text-2xl font-bold text-foreground">1</p>
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-border bg-card p-6">
          <div className="mb-4 flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search by device or agent name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full rounded-lg border border-input bg-background py-2 pl-10 pr-4 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All status</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
                <SelectItem value="reconciled">Reconciled</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-3">
            {collect_sessions.map((collection) => (
                <Link key={collection.id} href={`/admin/collection/sessions/${collection.id}`}>
                  <div className="rounded-lg border border-border p-4 transition-colors hover:bg-secondary/50">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <p className="text-lg font-semibold text-foreground">{collection.device?.name}</p>
                          <span
                              className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                                  collection.status === "open"
                                      ? "bg-success/10 text-success"
                                      : collection.status === "closed"
                                          ? "bg-warning/10 text-warning"
                                          : "bg-muted text-muted-foreground"
                              }`}
                          >
                        {collection.status}
                      </span>
                        </div>

                        <p className="mt-1 text-sm text-muted-foreground">
                          Agent: {collection.user!.firstName} {collection.user!.lastName}
                        </p>

                        <div className="mt-3 grid gap-4 md:grid-cols-5">
                          <div>
                            <p className="text-xs text-muted-foreground">Start Time</p>
                            <p className="mt-1 text-sm font-semibold text-foreground">{collection.start_at}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Collected Today</p>
                            <p className="mt-1 text-sm font-semibold text-success">
                              0 FCFA
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Visits</p>
                            <p className="mt-1 text-sm font-semibold text-foreground">0</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Active Devices</p>
                            <p className="mt-1 text-sm font-semibold text-foreground">0</p>
                          </div>
                        </div>

                        <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
                          <MapPin className="h-3 w-3" />
                          0
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
            ))}
          </div>
        </div>

      </div>
    </>
  )
}
